import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402/api';

// Trigger Types
export enum TriggerType {
  EVENT = 'event',
  SCHEDULE = 'schedule',
  MANUAL = 'manual',
}

// Event Types (matching backend webhook events)
export enum EventType {
  MEMBERSHIP_CREATED = 'membership.created',
  MEMBERSHIP_UPDATED = 'membership.updated',
  MEMBERSHIP_CANCELLED = 'membership.cancelled',
  MEMBERSHIP_RENEWED = 'membership.renewed',
  PAYMENT_SUCCEEDED = 'payment.succeeded',
  PAYMENT_FAILED = 'payment.failed',
  PAYMENT_REFUNDED = 'payment.refunded',
  POST_PUBLISHED = 'post.published',
  POST_UPDATED = 'post.updated',
  POST_DELETED = 'post.deleted',
  COMMENT_CREATED = 'comment.created',
  LIKE_CREATED = 'like.created',
  FOLLOW_CREATED = 'follow.created',
  MILESTONE_REACHED = 'milestone.reached',
}

// Action Types
export enum ActionType {
  SEND_MESSAGE = 'send_message',
  TRIGGER_WEBHOOK = 'trigger_webhook',
  CREATE_POST = 'create_post',
  SEND_EMAIL = 'send_email',
  ADD_TAG = 'add_tag',
  TRIGGER_INTEGRATION = 'trigger_integration',
}

// Condition Operators
export enum ConditionOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
}

// TypeScript Interfaces
export interface TriggerConfig {
  type: TriggerType;
  eventType?: EventType;
  cronSchedule?: string;
  timezone?: string;
}

export interface Condition {
  field: string;
  operator: ConditionOperator;
  value: any;
}

export interface Action {
  type: ActionType;
  config: {
    integrationId?: string;
    webhookId?: string;
    message?: string;
    subject?: string;
    template?: string;
    tag?: string;
    [key: string]: any;
  };
}

export interface Automation {
  id: string;
  userId: string;
  name: string;
  description?: string;
  triggerType: TriggerType;
  triggerConfig: TriggerConfig;
  conditions: Condition[];
  actions: Action[];
  isActive: boolean;
  isTemplate: boolean;
  executionCount: number;
  lastExecutedAt?: string;
  createdAt: string;
  updatedAt: string;
  executions?: AutomationExecution[];
  _count?: {
    executions: number;
  };
}

export interface AutomationExecution {
  id: string;
  automationId: string;
  status: 'success' | 'error' | 'skipped';
  triggerData?: any;
  actionResults?: any;
  errorMessage?: string;
  duration?: number;
  createdAt: string;
}

export interface AutomationStats {
  totalExecutions: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
  successRate: number;
  averageDuration: number;
  lastExecutedAt?: string;
}

export interface AutomationTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  triggerType: TriggerType;
  triggerConfig: TriggerConfig;
  conditions: Condition[];
  actions: Action[];
}

export interface CreateAutomationInput {
  name: string;
  description?: string;
  triggerType: TriggerType;
  triggerConfig: TriggerConfig;
  conditions: Condition[];
  actions: Action[];
  isActive?: boolean;
}

export interface UpdateAutomationInput {
  name?: string;
  description?: string;
  triggerType?: TriggerType;
  triggerConfig?: TriggerConfig;
  conditions?: Condition[];
  actions?: Action[];
  isActive?: boolean;
}

// API Client
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Get all automation templates
 */
export const getAutomationTemplates = async (): Promise<AutomationTemplate[]> => {
  const response = await axios.get(`${API_BASE_URL}/automations/templates`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Get a specific automation template
 */
export const getAutomationTemplate = async (templateId: string): Promise<AutomationTemplate> => {
  const response = await axios.get(`${API_BASE_URL}/automations/templates/${templateId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Create automation from template
 */
export const createAutomationFromTemplate = async (
  templateId: string,
  customization?: Partial<CreateAutomationInput>
): Promise<Automation> => {
  const response = await axios.post(
    `${API_BASE_URL}/automations/from-template`,
    { templateId, customization },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Get all automations for current user
 */
export const getAutomations = async (filters?: {
  isActive?: boolean;
  triggerType?: TriggerType;
  isTemplate?: boolean;
}): Promise<Automation[]> => {
  const params = new URLSearchParams();
  if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
  if (filters?.triggerType) params.append('triggerType', filters.triggerType);
  if (filters?.isTemplate !== undefined) params.append('isTemplate', String(filters.isTemplate));

  const response = await axios.get(`${API_BASE_URL}/automations?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Get a specific automation
 */
export const getAutomation = async (id: string): Promise<Automation> => {
  const response = await axios.get(`${API_BASE_URL}/automations/${id}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Create a new automation
 */
export const createAutomation = async (input: CreateAutomationInput): Promise<Automation> => {
  const response = await axios.post(
    `${API_BASE_URL}/automations`,
    input,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Update an automation
 */
export const updateAutomation = async (
  id: string,
  input: UpdateAutomationInput
): Promise<Automation> => {
  const response = await axios.put(
    `${API_BASE_URL}/automations/${id}`,
    input,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Delete an automation
 */
export const deleteAutomation = async (id: string): Promise<void> => {
  await axios.delete(`${API_BASE_URL}/automations/${id}`, {
    headers: getAuthHeaders(),
  });
};

/**
 * Toggle automation active status
 */
export const toggleAutomation = async (id: string, isActive: boolean): Promise<Automation> => {
  const response = await axios.patch(
    `${API_BASE_URL}/automations/${id}/toggle`,
    { isActive },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Manually execute an automation
 */
export const executeAutomation = async (
  id: string,
  triggerData?: any
): Promise<{
  execution: AutomationExecution;
  results?: any[];
  message: string;
}> => {
  const response = await axios.post(
    `${API_BASE_URL}/automations/${id}/execute`,
    { triggerData },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Get execution history for an automation
 */
export const getAutomationExecutions = async (
  id: string,
  options?: {
    limit?: number;
    offset?: number;
    status?: string;
  }
): Promise<{
  executions: AutomationExecution[];
  total: number;
  limit: number;
  offset: number;
}> => {
  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.offset) params.append('offset', String(options.offset));
  if (options?.status) params.append('status', options.status);

  const response = await axios.get(
    `${API_BASE_URL}/automations/${id}/executions?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

/**
 * Get statistics for an automation
 */
export const getAutomationStats = async (id: string): Promise<AutomationStats> => {
  const response = await axios.get(`${API_BASE_URL}/automations/${id}/stats`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// ========== Helper Functions ==========

/**
 * Get human-readable trigger type label
 */
export const getTriggerTypeLabel = (type: TriggerType): string => {
  const labels: Record<TriggerType, string> = {
    [TriggerType.EVENT]: 'When an event occurs',
    [TriggerType.SCHEDULE]: 'On a schedule',
    [TriggerType.MANUAL]: 'Manual trigger only',
  };
  return labels[type] || type;
};

/**
 * Get human-readable event type label
 */
export const getEventTypeLabel = (eventType: EventType): string => {
  const labels: Record<EventType, string> = {
    [EventType.MEMBERSHIP_CREATED]: 'New patron subscribed',
    [EventType.MEMBERSHIP_UPDATED]: 'Patron updated membership',
    [EventType.MEMBERSHIP_CANCELLED]: 'Patron cancelled',
    [EventType.MEMBERSHIP_RENEWED]: 'Subscription renewed',
    [EventType.PAYMENT_SUCCEEDED]: 'Payment succeeded',
    [EventType.PAYMENT_FAILED]: 'Payment failed',
    [EventType.PAYMENT_REFUNDED]: 'Payment refunded',
    [EventType.POST_PUBLISHED]: 'New post published',
    [EventType.POST_UPDATED]: 'Post updated',
    [EventType.POST_DELETED]: 'Post deleted',
    [EventType.COMMENT_CREATED]: 'New comment',
    [EventType.LIKE_CREATED]: 'New like',
    [EventType.FOLLOW_CREATED]: 'New follower',
    [EventType.MILESTONE_REACHED]: 'Milestone reached',
  };
  return labels[eventType] || eventType;
};

/**
 * Get human-readable action type label
 */
export const getActionTypeLabel = (type: ActionType): string => {
  const labels: Record<ActionType, string> = {
    [ActionType.SEND_MESSAGE]: 'Send direct message',
    [ActionType.TRIGGER_WEBHOOK]: 'Trigger webhook',
    [ActionType.CREATE_POST]: 'Create a post',
    [ActionType.SEND_EMAIL]: 'Send email',
    [ActionType.ADD_TAG]: 'Add patron tag',
    [ActionType.TRIGGER_INTEGRATION]: 'Trigger integration',
  };
  return labels[type] || type;
};

/**
 * Get status badge color
 */
export const getExecutionStatusColor = (status: string): string => {
  switch (status) {
    case 'success':
      return 'green';
    case 'error':
      return 'red';
    case 'skipped':
      return 'gray';
    default:
      return 'gray';
  }
};

/**
 * Format cron schedule to human-readable text
 */
export const formatCronSchedule = (cron: string): string => {
  // Basic cron to text conversion (simplified)
  const parts = cron.split(' ');
  if (parts.length !== 5) return cron;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Daily at specific time
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return `Daily at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  }

  // Weekly on specific day
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return `Weekly on ${days[parseInt(dayOfWeek)]} at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  }

  // Monthly on specific day
  if (dayOfMonth !== '*' && month === '*') {
    return `Monthly on day ${dayOfMonth} at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  }

  return cron;
};
